import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAppScope } from "./AppCover";

/** The tab bar and the air above it, which a long menu stops short of. */
const TAB_BAR = 84;

/*
  What the Space bar and Dashboard picker open on phones and tablets: Figma's Scrim from the
  control's bottom edge down, and the raised menu 8 under it, as wide as `anchorRef`. It is
  portaled because a page's panel is transformed, which would trap a fixed scrim inside it.
  `maxHeight` is the phone's scroll frame.
*/
const NavDropdown = ({ anchorRef, isDark, maxHeight, onClose, children }) => {
  const [box, setBox] = useState(null);
  const app = useAppScope();

  useLayoutEffect(() => {
    const measure = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const room = window.innerHeight - rect.bottom - 8 - TAB_BAR;
      setBox({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
        height: window.innerWidth < 600 ? Math.min(maxHeight, room) : room,
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [anchorRef, maxHeight]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!box) return null;

  return createPortal(
    <div data-theme={isDark ? "dark" : "light"} data-app={app || undefined} className="font-ds text-label-primary lg:hidden">
      <div
        className="fixed inset-x-0 bottom-0 z-[35] bg-overlay-scrim animate-ds-fade-in"
        style={{ top: box.top }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="fixed z-[35] overflow-y-auto overscroll-contain rounded-radius-lg border border-separator-default bg-bg-raised p-space-12 shadow-overlay animate-ds-pop-in"
        style={{ top: box.top + 8, left: box.left, width: box.width, maxHeight: box.height }}
        // Choosing a page closes it. A button that keeps it open stops the click itself.
        onClick={(event) => event.target.closest("button, a") && onClose()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default NavDropdown;
